/* =====================================================================
 * docxPatcher.ts — 편집된 블록만 원본 OOXML에 패치하여 새 DOCX 생성
 *
 * 전략:
 * 1. JSZip으로 원본 DOCX 열기
 * 2. dirty 블록만 순회하여 해당 w:p / w:tbl XML을 재생성
 * 3. document.xml의 해당 부분만 교체
 * 4. 나머지 파트(media, styles, rels 등)는 원본 그대로 유지
 * 5. JSZip.generateAsync로 새 DOCX Blob 생성
 * ===================================================================== */

import type {
  DocxDocument,
  DocxBlock,
  DocxParagraph,
  DocxTable,
  DocxRun,
  DocxTableCell,
} from './docxParser';

/* ------------------------------------------------------------------ */
/* XML 네임스페이스 접두어                                                */
/* ------------------------------------------------------------------ */

const W_PREFIX = 'w';
const R_PREFIX = 'r';

/* ------------------------------------------------------------------ */
/* 메인 패치 함수                                                       */
/* ------------------------------------------------------------------ */

/**
 * 편집된 문서를 새 DOCX Blob으로 생성.
 * dirty 블록이 없으면 원본 그대로 반환.
 */
export async function patchDocx(
  doc: DocxDocument,
  dirtyBlockIds: Set<string>,
): Promise<Blob> {
  // dirty 블록이 없으면 원본 그대로
  if (dirtyBlockIds.size === 0) {
    const blob = await doc.zip.generateAsync({ type: 'blob' });
    return blob;
  }

  // 원본 document.xml 가져오기
  const originalXml = doc.rawParts.get('word/document.xml');
  if (!originalXml) {
    throw new Error('원본 document.xml을 찾을 수 없습니다.');
  }

  // XML DOM 파싱
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(originalXml, 'application/xml');

  const bodyEl = xmlDoc.getElementsByTagNameNS(
    'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
    'body',
  )[0];
  if (!bodyEl) {
    throw new Error('document.xml에서 body를 찾을 수 없습니다.');
  }

  // 원본 body의 자식 요소 수집 (w:p, w:tbl 순서)
  const originalBlocks: Element[] = [];
  for (let i = 0; i < bodyEl.childNodes.length; i++) {
    const node = bodyEl.childNodes[i];
    if (node.nodeType !== 1) continue;
    const el = node as Element;
    if (
      (el.localName === 'p' || el.localName === 'tbl') &&
      el.namespaceURI === 'http://schemas.openxmlformats.org/wordprocessingml/2006/main'
    ) {
      originalBlocks.push(el);
    }
  }

  // 파싱된 내부 모델과 원본 XML 블록을 1:1 매핑하여 dirty 블록만 교체
  let blockIndex = 0;
  for (const block of doc.body) {
    if (blockIndex >= originalBlocks.length) break;

    const isDirty = dirtyBlockIds.has(block.id) || block.dirty;

    if (isDirty) {
      const originalEl = originalBlocks[blockIndex];
      const newEl = blockToXml(xmlDoc, block);
      if (newEl) {
        bodyEl.replaceChild(newEl, originalEl);
      }
    }

    blockIndex++;
  }

  // 새 XML 문자열 생성
  const serializer = new XMLSerializer();
  const newXml = serializer.serializeToString(xmlDoc);

  // ZIP에 교체
  const zip = doc.zip;
  zip.file('word/document.xml', newXml);

  // 새 DOCX Blob 생성
  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  return blob;
}

/* ------------------------------------------------------------------ */
/* 블록 → XML 변환                                                     */
/* ------------------------------------------------------------------ */

function blockToXml(xmlDoc: Document, block: DocxBlock): Element | null {
  if (block.type === 'paragraph') {
    return paragraphToXml(xmlDoc, block);
  }
  if (block.type === 'table') {
    return tableToXml(xmlDoc, block);
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* 단락 → XML                                                          */
/* ------------------------------------------------------------------ */

function paragraphToXml(xmlDoc: Document, para: DocxParagraph): Element {
  const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const pEl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:p`);

  // pPr (단락 속성)
  const pPr = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:pPr`);
  let hasPPr = false;

  // 스타일
  if (para.styleId) {
    const pStyle = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:pStyle`);
    pStyle.setAttribute(`${W_PREFIX}:val`, para.styleId);
    pPr.appendChild(pStyle);
    hasPPr = true;
  }

  // 정렬
  if (para.alignment) {
    const jc = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:jc`);
    jc.setAttribute(`${W_PREFIX}:val`, para.alignment);
    pPr.appendChild(jc);
    hasPPr = true;
  }

  // 들여쓰기
  if (para.indent) {
    const ind = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:ind`);
    if (para.indent.left !== undefined) ind.setAttribute(`${W_PREFIX}:left`, String(pxToTwips(para.indent.left)));
    if (para.indent.right !== undefined) ind.setAttribute(`${W_PREFIX}:right`, String(pxToTwips(para.indent.right)));
    if (para.indent.firstLine !== undefined) ind.setAttribute(`${W_PREFIX}:firstLine`, String(pxToTwips(para.indent.firstLine)));
    if (para.indent.hanging !== undefined) ind.setAttribute(`${W_PREFIX}:hanging`, String(pxToTwips(para.indent.hanging)));
    pPr.appendChild(ind);
    hasPPr = true;
  }

  // 간격
  if (para.spacing) {
    const sp = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:spacing`);
    if (para.spacing.before !== undefined) sp.setAttribute(`${W_PREFIX}:before`, String(pxToTwips(para.spacing.before)));
    if (para.spacing.after !== undefined) sp.setAttribute(`${W_PREFIX}:after`, String(pxToTwips(para.spacing.after)));
    if (para.spacing.line !== undefined) sp.setAttribute(`${W_PREFIX}:line`, String(para.spacing.line));
    pPr.appendChild(sp);
    hasPPr = true;
  }

  // 번호 매기기
  if (para.numbering) {
    const numPr = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:numPr`);
    const ilvl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:ilvl`);
    ilvl.setAttribute(`${W_PREFIX}:val`, String(para.numbering.level));
    numPr.appendChild(ilvl);
    const numId = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:numId`);
    numId.setAttribute(`${W_PREFIX}:val`, para.numbering.numId);
    numPr.appendChild(numId);
    pPr.appendChild(numPr);
    hasPPr = true;
  }

  if (hasPPr) {
    pEl.appendChild(pPr);
  }

  // 런
  for (const run of para.runs) {
    const rEl = runToXml(xmlDoc, run);
    if (rEl) pEl.appendChild(rEl);
  }

  return pEl;
}

/* ------------------------------------------------------------------ */
/* 런 → XML                                                            */
/* ------------------------------------------------------------------ */

function runToXml(xmlDoc: Document, run: DocxRun): Element | null {
  const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

  if (run.type === 'break') {
    const rEl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:r`);
    const br = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:br`);
    rEl.appendChild(br);
    return rEl;
  }

  if (run.type === 'tab') {
    const rEl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:r`);
    const tab = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tab`);
    rEl.appendChild(tab);
    return rEl;
  }

  if (run.type === 'image') {
    // 이미지는 원본 XML을 보존하는 것이 최선이므로 간단한 placeholder
    // 실제 이미지 편집은 지원하지 않으므로, 원본 유지를 위해 null 반환
    // → 원본 XML이 이미 보존되어 있으므로 dirty가 아닌 경우 교체되지 않음
    return null;
  }

  // type === 'text'
  const rEl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:r`);

  // rPr (런 속성)
  const rPr = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:rPr`);
  let hasRPr = false;

  if (run.bold) {
    const b = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:b`);
    rPr.appendChild(b);
    hasRPr = true;
  }

  if (run.italic) {
    const i = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:i`);
    rPr.appendChild(i);
    hasRPr = true;
  }

  if (run.underline) {
    const u = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:u`);
    u.setAttribute(`${W_PREFIX}:val`, 'single');
    rPr.appendChild(u);
    hasRPr = true;
  }

  if (run.strike) {
    const strike = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:strike`);
    rPr.appendChild(strike);
    hasRPr = true;
  }

  if (run.fontSize) {
    const sz = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:sz`);
    sz.setAttribute(`${W_PREFIX}:val`, String(run.fontSize));
    rPr.appendChild(sz);
    const szCs = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:szCs`);
    szCs.setAttribute(`${W_PREFIX}:val`, String(run.fontSize));
    rPr.appendChild(szCs);
    hasRPr = true;
  }

  if (run.fontFamily) {
    const rFonts = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:rFonts`);
    rFonts.setAttribute(`${W_PREFIX}:ascii`, run.fontFamily);
    rFonts.setAttribute(`${W_PREFIX}:hAnsi`, run.fontFamily);
    rFonts.setAttribute(`${W_PREFIX}:eastAsia`, run.fontFamily);
    rPr.appendChild(rFonts);
    hasRPr = true;
  }

  if (run.color) {
    const color = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:color`);
    // #RRGGBB → RRGGBB
    const colorVal = run.color.startsWith('#') ? run.color.slice(1) : run.color;
    color.setAttribute(`${W_PREFIX}:val`, colorVal);
    rPr.appendChild(color);
    hasRPr = true;
  }

  if (run.highlight) {
    const highlight = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:highlight`);
    highlight.setAttribute(`${W_PREFIX}:val`, run.highlight);
    rPr.appendChild(highlight);
    hasRPr = true;
  }

  if (hasRPr) {
    rEl.appendChild(rPr);
  }

  // 텍스트
  const t = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:t`);
  // 공백 보존
  t.setAttribute('xml:space', 'preserve');
  t.textContent = run.text || '';
  rEl.appendChild(t);

  return rEl;
}

/* ------------------------------------------------------------------ */
/* 테이블 → XML                                                        */
/* ------------------------------------------------------------------ */

function tableToXml(xmlDoc: Document, table: DocxTable): Element {
  const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const tblEl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tbl`);

  // 테이블 속성
  const tblPr = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tblPr`);
  const tblW = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tblW`);
  tblW.setAttribute(`${W_PREFIX}:w`, '0');
  tblW.setAttribute(`${W_PREFIX}:type`, 'auto');
  tblPr.appendChild(tblW);

  // 기본 테두리
  const tblBorders = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tblBorders`);
  for (const side of ['top', 'left', 'bottom', 'right', 'insideH', 'insideV']) {
    const border = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:${side}`);
    border.setAttribute(`${W_PREFIX}:val`, 'single');
    border.setAttribute(`${W_PREFIX}:sz`, '4');
    border.setAttribute(`${W_PREFIX}:space`, '0');
    border.setAttribute(`${W_PREFIX}:color`, 'auto');
    tblBorders.appendChild(border);
  }
  tblPr.appendChild(tblBorders);
  tblEl.appendChild(tblPr);

  // 행
  for (const row of table.rows) {
    const trEl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tr`);

    for (const cell of row.cells) {
      const tcEl = cellToXml(xmlDoc, cell);
      trEl.appendChild(tcEl);
    }

    tblEl.appendChild(trEl);
  }

  return tblEl;
}

/* ------------------------------------------------------------------ */
/* 셀 → XML                                                            */
/* ------------------------------------------------------------------ */

function cellToXml(xmlDoc: Document, cell: DocxTableCell): Element {
  const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
  const tcEl = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tc`);

  // tcPr
  const tcPr = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tcPr`);
  let hasTcPr = false;

  if (cell.width) {
    const tcW = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:tcW`);
    tcW.setAttribute(`${W_PREFIX}:w`, String(pxToTwips(cell.width)));
    tcW.setAttribute(`${W_PREFIX}:type`, 'dxa');
    tcPr.appendChild(tcW);
    hasTcPr = true;
  }

  if (cell.colSpan && cell.colSpan > 1) {
    const gridSpan = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:gridSpan`);
    gridSpan.setAttribute(`${W_PREFIX}:val`, String(cell.colSpan));
    tcPr.appendChild(gridSpan);
    hasTcPr = true;
  }

  if (cell.shading) {
    const shd = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:shd`);
    shd.setAttribute(`${W_PREFIX}:val`, 'clear');
    const fill = cell.shading.startsWith('#') ? cell.shading.slice(1) : cell.shading;
    shd.setAttribute(`${W_PREFIX}:fill`, fill);
    shd.setAttribute(`${W_PREFIX}:color`, 'auto');
    tcPr.appendChild(shd);
    hasTcPr = true;
  }

  if (hasTcPr) {
    tcEl.appendChild(tcPr);
  }

  // 셀 내 단락
  for (const para of cell.paragraphs) {
    tcEl.appendChild(paragraphToXml(xmlDoc, para));
  }

  // 최소 하나의 w:p 필수
  if (cell.paragraphs.length === 0) {
    const emptyP = xmlDoc.createElementNS(W_NS, `${W_PREFIX}:p`);
    tcEl.appendChild(emptyP);
  }

  return tcEl;
}

/* ------------------------------------------------------------------ */
/* 유틸리티                                                              */
/* ------------------------------------------------------------------ */

/** px → twips 변환 */
function pxToTwips(px: number): number {
  return Math.round((px / 96) * 1440);
}
